import { styled, alpha, keyframes } from "@mui/material/styles";

// Enhanced floating animation
const float = keyframes`
  0%, 100% {
    transform: translateY(0px) rotate(0deg);
  }
  25% {
    transform: translateY(-10px) rotate(0.5deg);
  }
  50% {
    transform: translateY(-5px) rotate(0deg);
  }
  75% {
    transform: translateY(-15px) rotate(-0.5deg);
  }
`;

// Subtle pulse animation
const pulse = keyframes`
  0%, 100% {
    opacity: 0.8;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.02);
  }
`;

// Gradient shift animation
const gradientShift = keyframes`
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
`;

// Enhanced shimmer effect
const shimmer = keyframes`
  0% {
    transform: translateX(-100%) translateY(-100%) rotate(30deg);
  }
  100% {
    transform: translateX(100%) translateY(100%) rotate(30deg);
  }
`;

const AnimatedContainer = styled("div")(({ theme }) => ({
  position: "relative",
  display: "flex",
  flexDirection: "column",
  justifyContent: "flex-start",
  alignItems: "center",
  overflow: "hidden",
  padding: theme.spacing(2, 3),
  borderRadius: theme.shape.borderRadius * 3,
  margin: theme.spacing(1, 1, 2, 1),
  minHeight: "90vh",
  width: "100%",

  // Enhanced background with better glassmorphism
  background:
    theme.palette.mode === "dark"
      ? `linear-gradient(135deg, 
        ${alpha(theme.palette.background.default, 0.95)} 0%, 
        ${alpha(theme.palette.background.paper, 0.85)} 25%,
        ${alpha(theme.palette.background.default, 0.9)} 75%,
        ${alpha(theme.palette.background.paper, 0.95)} 100%
      )`
      : `linear-gradient(135deg, 
        ${alpha(theme.palette.background.paper, 0.98)} 0%, 
        ${alpha(theme.palette.background.default, 0.95)} 25%,
        ${alpha(theme.palette.background.paper, 0.98)} 75%,
        ${alpha(theme.palette.background.default, 0.95)} 100%
      )`,

  // Enhanced backdrop filter
  backdropFilter: "blur(20px) saturate(180%)",
  WebkitBackdropFilter: "blur(20px) saturate(180%)",

  // Subtle border with gradient
  border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
  borderImage:
    theme.palette.mode === "dark"
      ? `linear-gradient(135deg, ${alpha(
          theme.palette.primary.main,
          0.2
        )}, ${alpha(theme.palette.secondary.main, 0.2)}) 1`
      : `linear-gradient(135deg, ${alpha(
          theme.palette.primary.light,
          0.3
        )}, ${alpha(theme.palette.secondary.light, 0.3)}) 1`,

  // Enhanced shadows
  boxShadow:
    theme.palette.mode === "dark"
      ? `
        0 8px 32px ${alpha(theme.palette.common.black, 0.4)},
        0 4px 16px ${alpha(theme.palette.primary.main, 0.1)},
        inset 0 1px 0 ${alpha(theme.palette.common.white, 0.1)}
      `
      : `
        0 8px 32px ${alpha(theme.palette.common.black, 0.08)},
        0 4px 16px ${alpha(theme.palette.primary.main, 0.05)},
        inset 0 1px 0 ${alpha(theme.palette.common.white, 0.7)}
      `,

  // Smooth transitions
  transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",

  // Enhanced animation
  //   animation: `${float} 8s ease-in-out infinite`,

  // Responsive design
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1.5, 2),
    margin: theme.spacing(0.5, 0.5, 1, 0.5),
    borderRadius: theme.shape.borderRadius * 2,
    minHeight: "85vh",
  },

  [theme.breakpoints.down("xs")]: {
    padding: theme.spacing(1, 1.5),
    margin: theme.spacing(0.5),
    borderRadius: theme.shape.borderRadius * 1.5,
  },

  // Hover effects
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow:
      theme.palette.mode === "dark"
        ? `
          0 12px 48px ${alpha(theme.palette.common.black, 0.5)},
          0 8px 24px ${alpha(theme.palette.primary.main, 0.15)},
          inset 0 1px 0 ${alpha(theme.palette.common.white, 0.15)}
        `
        : `
          0 12px 48px ${alpha(theme.palette.common.black, 0.12)},
          0 8px 24px ${alpha(theme.palette.primary.main, 0.08)},
          inset 0 1px 0 ${alpha(theme.palette.common.white, 0.8)}
        `,
  },

  // Before pseudo-element for additional effects
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background:
      theme.palette.mode === "dark"
        ? `radial-gradient(circle at 20% 20%, ${alpha(
            theme.palette.primary.main,
            0.1
          )} 0%, transparent 50%),
         radial-gradient(circle at 80% 80%, ${alpha(
           theme.palette.secondary.main,
           0.1
         )} 0%, transparent 50%)`
        : `radial-gradient(circle at 20% 20%, ${alpha(
            theme.palette.primary.light,
            0.05
          )} 0%, transparent 50%),
         radial-gradient(circle at 80% 80%, ${alpha(
           theme.palette.secondary.light,
           0.05
         )} 0%, transparent 50%)`,
    borderRadius: "inherit",
    pointerEvents: "none",
    animation: `${pulse} 6s ease-in-out infinite`,
  },

  // Shimmer effect on certain conditions
  "&.loading::after": {
    content: '""',
    position: "absolute",
    top: "-50%",
    left: "-50%",
    width: "200%",
    height: "200%",
    background: `linear-gradient(45deg, transparent 30%, ${alpha(
      theme.palette.common.white,
      0.1
    )} 50%, transparent 70%)`,
    animation: `${shimmer} 2s infinite`,
    pointerEvents: "none",
  },
}));

// Enhanced background with more sophisticated animations
const AnimatedBackground = styled("div")(({ theme }) => ({
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  overflow: "hidden",
  borderRadius: "inherit",
  pointerEvents: "none",
  zIndex: -1,

  // Enhanced gradient background
  background:
    theme.palette.mode === "dark"
      ? `linear-gradient(135deg,
        ${alpha(theme.palette.primary.dark, 0.15)} 0%,
        ${alpha(theme.palette.secondary.dark, 0.1)} 25%,
        ${alpha(theme.palette.primary.main, 0.08)} 50%,
        ${alpha(theme.palette.secondary.main, 0.12)} 75%,
        ${alpha(theme.palette.primary.dark, 0.15)} 100%
      )`
      : `linear-gradient(135deg,
        ${alpha(theme.palette.primary.light, 0.08)} 0%,
        ${alpha(theme.palette.secondary.light, 0.05)} 25%,
        ${alpha(theme.palette.primary.main, 0.04)} 50%,
        ${alpha(theme.palette.secondary.main, 0.06)} 75%,
        ${alpha(theme.palette.primary.light, 0.08)} 100%
      )`,

  backgroundSize: "400% 400%",
  animation: `${gradientShift} 15s ease infinite`,

  // Multiple decorative elements
  "&::before": {
    content: '""',
    position: "absolute",
    top: "10%",
    left: "10%",
    width: "30%",
    height: "30%",
    background: `radial-gradient(circle, ${alpha(
      theme.palette.primary.main,
      0.1
    )} 0%, transparent 70%)`,
    borderRadius: "50%",
    // animation: `${float} 10s ease-in-out infinite reverse`,
  },

  "&::after": {
    content: '""',
    position: "absolute",
    bottom: "15%",
    right: "15%",
    width: "25%",
    height: "25%",
    background: `radial-gradient(circle, ${alpha(
      theme.palette.secondary.main,
      0.08
    )} 0%, transparent 70%)`,
    borderRadius: "50%",
    // animation: `${float} 12s ease-in-out infinite`,
  },
}));

// Enhanced particle system (optional, for special occasions)
const ParticleContainer = styled("div")(({ theme }) => ({
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  overflow: "hidden",
  pointerEvents: "none",
  zIndex: 1,

  "& .particle": {
    position: "absolute",
    width: "4px",
    height: "4px",
    background: alpha(theme.palette.primary.main, 0.3),
    borderRadius: "50%",
    // animation: `${float} 8s ease-in-out infinite`,
  },

  "& .particle:nth-of-type(2n)": {
    background: alpha(theme.palette.secondary.main, 0.3),
    animationDelay: "1s",
    animationDuration: "10s",
  },

  "& .particle:nth-of-type(3n)": {
    background: alpha(theme.palette.primary.light, 0.2),
    animationDelay: "2s",
    animationDuration: "12s",
  },
}));

export { AnimatedContainer, AnimatedBackground, ParticleContainer };
